import React, { useEffect, useState } from 'react';
import '@/i18n'; // Import to initialize i18n
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useColorModes } from '@coreui/react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '@/scss/style.scss';

import { store, persistor } from '@/store';
import { setAuthToken } from '@/libs/axios';
import { TranslationProvider } from '@/context/TranslationContext';
import { UserService } from '@/api';
import { addUser, clearUser } from '@/store/features/userSlice';

import App from './App';

// Initialize QueryClient for React Query
const queryClient = new QueryClient();

// Function to handle token setting and profile refresh before app lift
const handleOnBeforeLift = async () => {
  const accessToken = store.getState().user?.accessToken;

  if (accessToken) {
    setAuthToken(accessToken);

    try {
      const profileResponse = await UserService.getProfile();

      if (profileResponse?.data?.user) {
        store.dispatch(addUser({
          accessToken: accessToken,
          userData: profileResponse.data.user
        }));
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        store.dispatch(clearUser());
      }
    }
  }
};

/**
 * @name AppProviders
 * @description Wraps the App with all necessary providers
 */
const AppProviders = () => {
  const { setColorMode } = useColorModes('suit-me-color-theme');

  useEffect(() => {
    setColorMode('light');
  }, [setColorMode]);

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate
          loading={<div className="app-loading"><div className="spinner"></div></div>}
          persistor={persistor}
          onBeforeLift={handleOnBeforeLift}
        >
          <TranslationProvider>
            <ToastContainer />
            <App />
          </TranslationProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
};

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <AppProviders />
  </React.StrictMode>
);
